import { memo, useState, useCallback, useEffect } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'translations';
import { useRequest } from '@wappsto/blanket';
import { useDispatch } from 'react-redux';
import { addSession, REQUEST_ERROR } from '@wappsto/redux';
import ShowError from 'components/alerts/ShowError';
import Loader from 'components/helpers/Loader';
import Cookies from 'util/cookies';
import { LINKS } from 'configuration';
import FirebaseUIAuth from './FirebaseUIAuth';

const { Paragraph } = Typography;

const SignInOptions = memo(() => {
  const { tC } = useTranslation('features', 'authentication');
  const text = tC('acceptTermsWhenSignInMessage');
  const terms = tC('acceptTermsWhenSignInTerms');
  const components = text.split(new RegExp(`(${terms})`));

  const dispatch = useDispatch();
  const { request, send } = useRequest();
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  const fbSignIn = useCallback(
    (response): boolean => {
      setShowError(false);
      setLoading(true);
      send({
        method: 'POST',
        url: '/session',
        body: {
          firebase_token: response.user.accessToken,
          remember_me: true,
        },
      });
      return false;
    },
    [send],
  );

  const hideError = useCallback(() => {
    setShowError(false);
  }, []);

  useEffect(() => {
    if (request) {
      if (request.status === 'success') {
        if (request.json) {
          dispatch(addSession(request.json));
          Cookies.create('sessionID', request.json.meta.id, 30);
        } else {
          dispatch({
            type: REQUEST_ERROR,
            ...request,
          });
        }
      } else if (request.status === 'error') {
        setLoading(false);
        setShowError(true);
      }
    }
  }, [request, dispatch]);

  return (
    <>
      <Paragraph type="secondary">
        {components.map((str) => {
          if (str === terms) {
            return (
              <a key={str} target="_blank" rel="noopener noreferrer" href={LINKS.terms}>
                {str}
              </a>
            );
          }
          return <span key={str}>{str}</span>;
        })}
      </Paragraph>

      {loading && <Loader />}

      {showError && <ShowError request={request} />}

      <FirebaseUIAuth uiCallback={hideError} fbSignIn={fbSignIn} />
    </>
  );
});

SignInOptions.displayName = 'SignInOptions';

export default SignInOptions;
